import * as React from 'react';
import { AnyObject } from 'final-form';
import LeaseClause from 'src/models/LeaseClause';
import { Page, Text, Document, StyleSheet, View } from '@react-pdf/renderer';
import PropertyUtil from 'src/util/PropertyUtil';
import moment from 'moment';
import MathUtil from 'src/util/MathUtil';
// import SignatureBlockManager from './SignatureBlockManager';
import Acknowledgement from 'src/models/leasing/Acknowledgement';
import SignatureBlock from 'src/models/leasing/SignatureBlock';
import LeaseClauseType from 'src/models/LeaseClauseType';
import LocationModel from 'src/models/leasing/LocationModel';
import ContractAddendumClause from '../redesign/ContractGenerationModels/ContractAddendumClause';
import IContract from 'src/models/contract_types/IContract';

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#FFFFFF',
        paddingTop: 35,
        paddingBottom: 65,
        paddingHorizontal: 55,
    },
    pageNumbers: {
        position: 'absolute',
        bottom: 20,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 10,
        color: '#8e9094',
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
    },
    bold: {
        fontFamily: 'Times-Bold',
    },
    fontNormal: {
        fontFamily: 'Times-Roman',
        fontSize: 12,
        lineHeight: 1.25,
    },
    mt1: {
        marginTop: 10,
    },
    mt2: {
        marginTop: 20,
    },
    mt3: {
        marginTop: 30,
    },
    mt4: {
        marginTop: 40,
    },
    center: {
        textAlign: 'center',
    },
    text: {
        textAlign: 'justify',
    },
    sbTitle: {
        textAlign: 'center',
        marginBottom: 10,
    },
    underlined: {
        textDecoration: 'underline',
    },
    mailingInfo: {
        fontSize: 10,
        fontFamily: 'Times-Roman',
    },
    acknowledgementHeading: {
        textAlign: 'center',
        fontSize: '18',
        fontWeight: 'bold',
    },
    exhibitHeading: {
        textAlign: 'center',
        fontSize: 24,
        fontWeight: 'bold',
    },
    leaseHeading: {
        textAlign: 'center',
        fontSize: 20,
        fontFamily: 'Times-Bold',
    },
    doubleLines: {
        borderTop: '1px solid black',
        borderBottom: '1px solid black',
    },
    h2: {
        fontSize: 14,
    },
});

export interface IDemoLeaseStateProps {
    contractData: IContract;
    leaseClauses?: ContractAddendumClause[];
    leaseClauseTypes?: LeaseClauseType[];
    location?: LocationModel;
}

export default class DemoLease extends React.Component<IDemoLeaseStateProps> {
    private additionalProvisionNumber = 0;

    additionalProvisions = () => {
        let renderClauses: ContractAddendumClause[] = [];
        this.props.contractData?.contractClauses?.forEach((llc: AnyObject, index: number) => {
            let clause: ContractAddendumClause = !!this.props.leaseClauses
                ? this.props.leaseClauses?.find((lc) => lc.id === llc.contractAddendumClauseId)
                : undefined;

            if (!!clause) {
                renderClauses.push(clause);
            }
        });

        return renderClauses?.map((rc: LeaseClause, index: number) => {
            this.additionalProvisionNumber++;
            return (
                <Text key={index} style={[styles.fontNormal, styles.mt2, styles.text]}>
                    {this.additionalProvisionNumber}) {`${rc.leaseClauseType}: ${rc.clauseText}`}
                </Text>
            );
        });
    };

    adhocProvisions = () => {
        const adHocClauses = this?.props?.contractData?.adHocClauses;
        if (adHocClauses?.length > 0) {
            return this.props?.contractData?.adHocClauses?.map((lc: LeaseClause, index: number) => {
                let type: string = !!this.props?.leaseClauseTypes
                    ? this.props?.leaseClauseTypes?.find((lct) => lct.id === lc.leaseClauseTypeId)?.name
                    : '';

                this.additionalProvisionNumber++;
                return (
                    <Text key={index} style={[styles.fontNormal, styles.mt2, styles.text]}>
                        {this.additionalProvisionNumber}) {`${type}: ${lc.clauseText}`}
                    </Text>
                );
            });
        }
        return <div />;
    };

    displaySignatureBlocks = () => {
        return this.props?.contractData
            ? this.props?.contractData?.acknowledgements?.map((la: Acknowledgement) => {
                  return (
                      la.signatureBlocks.length > 0 &&
                      la.signatureBlocks.map((sb: SignatureBlock, index: number) => {
                          return (
                              <View key={index} wrap={false}>
                                  <Text style={[styles.fontNormal, styles.mt3, styles.text, styles.bold]}>{!!sb.title && sb?.title}</Text>
                                  <Text style={[styles.fontNormal, styles.mt2, styles.text]}>Lessor:</Text>
                                  <Text style={[styles.fontNormal, styles.mt2, styles.text, styles.bold]}>____________________________________</Text>
                                  <Text style={[styles.fontNormal, styles.mt1, styles.text]}>{sb.ownerName}</Text>
                              </View>
                          );
                      })
                  );
              })
            : [];
    };

    displayAcknowledgements = () => {
        return this.props?.contractData
            ? this.props?.contractData?.acknowledgements?.map((la: Acknowledgement, index: number) => {
                  return (
                      <View key={index} wrap={false}>
                          <Text style={[styles.fontNormal, styles.mt3, styles.text]}>{la.text}</Text>
                      </View>
                  );
              })
            : [];
    };

    // signatureBlocks = (leaseValues: AnyObject) => {
    //     return <SignatureBlockManager leaseValues={leaseValues} styles={styles} />;
    // };

    // tslint:disable: max-line-length
    render() {
        const leaseValues = PropertyUtil.SafeGet(() => this.props.contractData);
        const firstIncludedTract = leaseValues?.tracts?.find((tract: AnyObject) => tract.includeTract === true);
        const totalGrossAcres = leaseValues?.tracts?.reduce((acc: number, tract: AnyObject) => {
            if (tract.includeTract === true) {
                let tempGrossAcres = parseFloat(tract.grossAcres) || 0;
                return acc + tempGrossAcres;
            } else {
                return acc;
            }
        }, 0);

        return (
            <Document>
                <Page size="LEGAL" style={styles.page}>
                    <Text style={styles.mailingInfo}>After recording return to:</Text>
                    <Text style={styles.mailingInfo}>{PropertyUtil.SafeGet(() => leaseValues.lesseeName, 'LESSEE NAME')}</Text>
                    <Text style={styles.mailingInfo}>{PropertyUtil.SafeGet(() => leaseValues.lesseeAddressLine1, 'ADDRESS')}</Text>
                    <Text style={styles.mailingInfo}>{PropertyUtil.SafeGet(() => leaseValues.lesseeAddressLine2, '')}</Text>
                    <Text style={styles.mailingInfo}>
                        {PropertyUtil.SafeGet(
                            () => leaseValues.lesseeCity + ', ' + leaseValues.lesseeState + ' ' + leaseValues.lesseeZipCode,
                            'CITY, STATE, ZIP'
                        )}
                    </Text>

                    <Text style={[styles.leaseHeading, styles.mt3]}>OIL AND GAS LEASE</Text>
                    <Text style={[styles.center, styles.fontNormal, styles.mt1]}>(PAID UP)</Text>

                    <Text style={[styles.fontNormal, styles.mt4, styles.text]}>
                        AGREEMENT, Made and entered into this {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('Do'), 'DAY')} day
                        of {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('MMMM'), 'MONTH')},{' '}
                        {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('YYYY'), 'YEAR')}, by and between{' '}
                        {PropertyUtil.SafeGet(() => leaseValues.ownersOnDocument, 'LESSOR NAME')}, whose address is{' '}
                        {PropertyUtil.SafeGet(() => leaseValues.ownersAddressOnDocument, 'LESSOR ADDRESS')}, Party of the first part, hereinafter
                        called "Lessor" (whether one or more), and {PropertyUtil.SafeGet(() => leaseValues.lesseeName, 'LESSEE NAME')}, whose address
                        is{' '}
                        {PropertyUtil.SafeGet(
                            () =>
                                leaseValues.lesseeAddressLine1 +
                                ', ' +
                                leaseValues.lesseeCity +
                                ', ' +
                                leaseValues.lesseeState +
                                ', ' +
                                leaseValues.lesseeZipCode,
                            'LESSEE ADDRESS'
                        )}
                        , Party of the second part, hereinafter called "Lessee".
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        WITNESSETH, That said Lessor, for and in consideration of Ten and More ($10.00) DOLLARS, cash in hand paid, receipt of which
                        is hereby acknowledged and of the covenants and agreements hereinafter contained on the part of Lessee to be paid, kept and
                        performed, has granted, demised, leased and let and by these presents does grant, demise, lease and let exclusively unto the
                        said Lessee, for the sole and only purpose of exploring by geophysical and other methods, mining and operating for oil
                        (including but not limited to distillate and condensate), gas (including casinghead gas and helium and all other
                        constituents), and for laying pipe lines, and building tanks, power stations and structures thereon, to produce, save and take
                        care of said products, all that certain tract of land, together with any reversionary rights therein, situated in the State of
                        Texas, described as follows, to-wit:
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text, styles.bold, styles.underlined]}>
                        Township {PropertyUtil.SafeGet(() => firstIncludedTract?.township, 'TWP')}, Range{' '}
                        {PropertyUtil.SafeGet(() => firstIncludedTract?.range, 'RNG')}, Section{' '}
                        {PropertyUtil.SafeGet(() => firstIncludedTract?.section, 'SEC')}, I.M.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        {!leaseValues?.legalDescription
                            ? 'LEGAL DESCRIPTION'
                            : leaseValues?.legalDescription?.length > 1000
                            ? 'SEE EXHIBIT A ...'
                            : leaseValues?.legalDescription}
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt2, styles.text]}>
                        and containing ({PropertyUtil.SafeGet(() => totalGrossAcres, 'GROSS ACRES')}) acres more or less (the "Lands" or "Leased
                        Premises").
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        It is agreed that this lease shall remain in force for a term of{' '}
                        {PropertyUtil.SafeGet(() => MathUtil.numberToEnglish(parseInt(leaseValues?.leasehold?.primaryTerm, 10)), 'LEASE TERM WORD')} (
                        {PropertyUtil.SafeGet(() => leaseValues.term, 'LEASE TERM')}) {leaseValues?.leasehold?.primaryTermUnits} from{' '}
                        {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('MMMM-DD-YYYY'), 'LEASE DATE')} ( herein called primary
                        term) and so long thereafter as oil or gas, or either of them, is produced from said land by Lessee.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>In consideration of the premises Lessee covenants and agrees:</Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        1st. To deliver in-kind to Lessor, at the well, or to the credit of Lessor into the pipeline or storage tank to which the well
                        may be connected, a ({PropertyUtil.SafeGet(() => leaseValues?.leasehold?.royalty, 'Royalty')}) part of all oil (including
                        condensate) produced and saved from the leased premises. In lieu thereof, Lessee shall have the option, at any time, to sell
                        Lessor's oil, in which case Lessor's royalty shall be based on Lessee's gross proceeds at the mouth of the well, or to
                        purchase Lessor's oil for the value of such oil at the mouth of the well. In any event, Lessor's interest shall be free of all
                        costs of production, but shall bear its proportionate part of production and similar taxes, and shall share proportionately
                        with Lessee in any costs to market, transport, or condition the oil.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        2nd. To pay Lessor for gas (including casinghead gas and coal bed methane gas) of whatsoever nature or kind (with all of its
                        consituents) produced and sold or used off the Leased Premises, or used in the manufacture of products therefrom, (
                        {PropertyUtil.SafeGet(() => leaseValues?.leasehold?.royalty, 'Royalty')}) of the gross proceeds received from any party
                        (whether or not an affiliate of Lessee) for the gas sold, used off the Leased Premises or in the manufacture of products
                        therefrom, less a proportionate part of any production, severance and other excises taxes and costs and/or fees incurred by
                        Lessee in making marketable Lessor’s share of gas, and/or in gathering, transporting, processing, compressing or otherwise
                        marketing Lessor’s share of gas, but in no event more than (
                        {PropertyUtil.SafeGet(() => leaseValues?.leasehold?.royalty, 'Royalty')}) of the net amount actually received by Lessee from
                        any such party, said payments to be made monthly. During any period (whether before or after expiration of the primary term
                        hereof) when gas is not being so sold or used and the well or wells are shut in and there is no current production of oil or
                        operations on said Leased Premises sufficient to keep this lease in force, Lessee shall pay or tender a royalty of One Dollar
                        ($1.00) per year per net royalty acre retained here-under, such payment or tender to be made, on or before the anniversary
                        date of this lease next ensuing after the expiration of ninety (90) days from the date such well is shut in and thereafter on
                        the anniversary date of this lease during the period such well is shut in, to the royalty owners. When such payment or tender
                        is made it will be considered that gas is being produced within the meaning of the entire lease.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        If Lessee shall commence operations to drill a well or commence reworking operations on an existing well within the term of
                        this lease or any extension thereof, or on acreage pooled therewith, Lessee shall have the right to drill such well to
                        completion or complete reworking operations with reasonable diligence and dispatch, and if oil or gas, or either of them, be
                        found in paying quantities, this lease shall continue and be in force with like effect as if such well had been completed
                        within the term of years first mentioned.
                    </Text>

                    <Text
                        style={styles.pageNumbers}
                        render={({ pageNumber }) => {
                            return `${pageNumber}`;
                        }}
                        fixed={true}
                    />
                </Page>

                <Page size="LEGAL" style={styles.page}>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        Lessee is hereby granted the right at any time and from time to time to unitize the Leased Premises or any portion or portions
                        thereof, as to all strata or any stratum or strata, with any other lands as to all strata or any stratum or strata, for the
                        production primarily of oil or primarily of gas with or without distillate. However, no unit for the production primarily of
                        oil shall embrace more than 40 acres, or for the production primarily of gas with or without distillate more than 640 acres;
                        provided that if any governmental regulation shall prescribe a spacing pattern for the development of the field or allocate a
                        producing allowable based on acreage per well, then any such unit may embrace as much additional acreage as may be so
                        prescribed or as may be used in such allocation of allowable.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        Operations upon and production from the unit shall be treated as if such operations were upon or such production were from the
                        Leased Premises whether or not the well or wells are located thereon. The entire acreage within a unit shall be treated for
                        all purposes as if it were covered by and included in this lease except that the royalty on production from the unit shall be
                        as below provided, and except that in calculating the amount of any shut in gas royalties, only the part of the acreage
                        originally leased and then actually embraced by this lease shall be counted. In respect to production from the unit, Lessee
                        shall pay Lessor, in lieu of other royalties thereon, only such proportion of the royalties stipulated herein as the amount of
                        his acreage placed in the unit, or his royalty interest therein on an acreage basis bears to the total acreage in the unit.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        If said Lessor owns a less interest in the above described land than the entire and undivided fee simple estate therein, then
                        the royalties herein provided shall be paid to the Lessor only in the proportion which his interest bears to the whole and
                        undivided fee.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        Lessee shall have the right to use, free of cost, gas, oil and water produced on said land for its operations thereon, except
                        water from wells of Lessor.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        When requested by Lessor, Lessee shall bury its pipe lines below plow depth.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        No well shall be drilled nearer than 200 feet to the house or barn now on said premises, without the written consent of
                        Lessor. Lessee shall pay for all damages caused by its operations to growing crops on said land.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        Lessee shall have the right at any time to remove all machinery and fixtures placed on said premises, including the right to
                        draw and remove casing.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        If the estate of either Party hereto is assigned, and the privilege of assigning in whole or in part is expressly allowed, the
                        covenants hereof shall extend to their heirs, executors, administrators, successors or assigns. However, no change or division
                        in ownership of the land or royalties shall enlarge the obligations or diminish the rights of Lessee. No change in the
                        ownership of the land or royalties shall be binding on the Lessee until after the Lessee has been furnished with a written
                        transfer or assignment or a true copy thereof. In case Lessee assigns this lease, in whole or in part, Lessee shall be
                        relieved of all obligations with respect to the assigned portion or portions arising subsequent to the date of assignment.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        All express or implied covenants of this lease shall be subject to all Federal and State Laws, Executive Orders, Rules and
                        Regulations, and this lease shall not be terminated, in whole or in part, nor Lessee held liable in damages, for failure to
                        comply therewith, if compliance is prevented by, or such failure is the result of any such Law, Order, Rule or Regulation.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        This lease shall be effective as to each Lessor on execution hereof as to his or her interest and shall be binding on those
                        signing, notwithstanding some of the Lessors above named may not join in the execution hereof. The word “Lessor” as used in
                        this lease means the Party or Parties who execute this lease as Lessor, although not named above.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        Lessee may at any time and from time to time surrender this lease as to any part or parts of the Leased Premises by delivering
                        or mailing a release thereof to Lessor, or by placing a release of record in the proper County.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        Lessor specifically grants Lessee the right to conduct geophysical operations on the Lands covered by this lease and
                        acknowledges Lessee will own the seismic data resulting from said geophysical operations and is free to utilize the seismic
                        data for its sole and exclusive benefit. Lessor hereby grants and conveys to Lessee any and all rights in and to existing
                        wellbores located on the Lands described above, including the right to re-enter said wellbore(s) and utilize same for Lessees
                        benefit.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        If at any time within the primary term of this lease and while the same remains in force and effect, Lessor receives any bona
                        fide offer, acceptable to Lessor, to grant an additional lease (top lease) covering all or part of the aforementioned Lands,
                        Lessee shall have the continuing option by meeting any such offer to acquire such top lease. Any offer must be in writing, and
                        must set forth the proposed lessee's name, bonus consideration and royalty consideration to be paid for such lease and include
                        a copy of the lease form to be utilized which form should reflect all pertinent and relevant terms and conditions of the top
                        lease. Lessee shall have fifteen (15) days after receipt, from Lessor, of a complete copy of any such offer to advise Lessor
                        in writing of its election to enter into an oil and gas lease with Lessor on equivalent terms and conditions. If Lessee fails
                        to notify Lessor with the aforesaid fifteen (15) day period of its election to meet any such bona fide offer, Lessor shall
                        have the right to accept said offer. Any top lease granted by Lessor in violation of this provision shall be null and void.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        Lessor hereby warrants and agrees to defend the title to the Lands herein described, and agrees that Lessee shall have the
                        right at any time to redeem for Lessor by payment any mortgages, taxes or other liens on the above described Lands, in the
                        event of default of payment by Lessor, and be subrogated to the rights of the holder thereof.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        It is the intent of the Lessor to lease, and Lessor does hereby grant, demise, lease and let unto Lessee, all oil, gas and
                        other minerals owned by Lessor in Section ({PropertyUtil.SafeGet(() => firstIncludedTract?.section, 'SEC')}), Township (
                        {PropertyUtil.SafeGet(() => firstIncludedTract?.township, 'TWP')}), Range (
                        {PropertyUtil.SafeGet(() => firstIncludedTract?.range, 'RNG')}) whether or not properly and completely described herein. In
                        the event it is determined that Lessor actually owns more net mineral acres than that assumed by the parties in the
                        calculation of lease bonus and paid by Lessee, Lessor and Lessee agree that Lessee shall pay Lessor for such additional net
                        acreage at the same bonus price per acre agreed upon for the execution of this oil and gas lease. Likewise, in the event it is
                        determined that Lessor owns less net acres, or it is determined that Lessor's acreage is currently leased under a prior oil
                        and gas lease, then the Lessor agrees to reimburse Lessee for the bonus per acre paid for the acreage not owned by Lessor or
                        under the prior oil and gas lease.
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt4, styles.text, styles.bold, styles.center]}>
                        [SIGNATURES ARE DISPLAYED IN THE NEXT PAGE]
                    </Text>

                    <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                </Page>

                {/* <Page size="LEGAL" style={styles.page}>

                    <Text style={[styles.fontNormal, styles.mt2, styles.text]}>
                        IN TESTIMONY WHEREOF, I sign the ____ day of ___________________, 20____,
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt4, styles.text]}>
                        LESSOR:
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt4, styles.text]}>
                        ______________________________________________
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        {PropertyUtil.SafeGet(() => leaseValues.lessor.name, 'LESSOR')}
                    </Text>

                    <Text style={[styles.fontNormal, styles.center, styles.mt3, styles.h2]}>
                        (ACKNOWLEDGMENT)
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        STATE OF ________________________
                         </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        COUNTY OF ________________________
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        Before me, the udnersigned, a Notary Public, in and for said county and State, on this ____ day of ____________________, 20___, personally appeared ____________________, an
                        individual, personally known to me
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt1]}>
                        to be the identical person who executed the within and foregoinginstrument, and acknowledged to me that he executed the same as his free and voluntary act and deed, for the
                        use and purposes therein set forth.
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt1]}>
                        Notary Public
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        Print Name _______________________________________
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        My Commission expires: ________________________
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        Commission Number: ________________________
                        </Text>

                    <Text
                        style={styles.pageNumbers}
                        render={({ pageNumber }) => (
                            `${pageNumber}`
                        )}
                        fixed={true}
                    />

                    {this.signatureBlocks()}
                </Page> */}

                {PropertyUtil.SafeGet(() => leaseValues.acknowledgements.length > 0) && (
                    <Page size="LEGAL" style={styles.page}>
                        <Text style={[styles.fontNormal, styles.mt3, styles.sbTitle, styles.h2, styles.bold]}>
                            IN TESTIMONY WHEREOF, I sign the ____ day of ____________________, 20____.
                        </Text>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                rowGap: '5rem',
                            }}
                        >
                            {this.displaySignatureBlocks()}
                        </div>
                        <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                    </Page>
                )}
                {PropertyUtil.SafeGet(() => leaseValues.acknowledgements.length > 0) && (
                    <Page size="LEGAL" style={styles.page}>
                        <Text style={[styles.acknowledgementHeading, styles.mt3]}>(ACKNOWLEDGEMENT)</Text>
                        {this.displayAcknowledgements()}
                        <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                    </Page>
                )}

                {PropertyUtil.SafeGet(() => leaseValues?.legalDescription.length > 1000) && (
                    <Page size="LEGAL" style={styles.page}>
                        <Text style={styles.exhibitHeading}>Exhibit A</Text>
                        <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                            {PropertyUtil.SafeGet(() => leaseValues.legalDescription, 'LEGAL DESCRIPTION')}
                        </Text>
                        <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                    </Page>
                )}

                {PropertyUtil.SafeGet(() => leaseValues.adHocClauses.length > 0) && (
                    <Page size="LEGAL" style={styles.page}>
                        <Text style={styles.exhibitHeading}>Exhibit {leaseValues?.legalDescription.length > 1000 ? 'B' : 'A'}</Text>
                        <Text style={[styles.fontNormal, styles.mt3, styles.h2]}>Additional Provisions</Text>

                        {this.additionalProvisions()}
                        {this.adhocProvisions()}

                        <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                    </Page>
                )}
            </Document>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/LeaseDocuments/DemoLease.tsx